import React from 'react';
import LmsRolePage from './LmsRolePage';

const relatedLinks = [
  { label: 'LMS for ANSPs and CAAs', path: '/lms-ansp-caa' },
  { label: 'LMS for Aviation Companies', path: '/lms-aviation-companies' },
];

const LmsAirlinesPage: React.FC = () => {
  return (
    <LmsRolePage
      title="AviaGov LMS for Airlines"
      seoTitle="AviaGov LMS for Airlines"
      description="Airline-focused LMS with crew qualification profiles, external training imports, check-event workflows, recurrence alerts, and compliance-ready training records."
      eyebrow="Airlines"
      heroNote="Built for crew qualification governance, operational recurrence, and controlled airline record flows."
      heroImagePrompt="A modern airline operations training command center with crew qualification profiles, check-event timelines, recurrence alerts, and compliance review boards, dark premium aviation style, no branding"
      heroImageAlt="Airline operations training command center showing crew qualification profiles, recurrence alerts, and compliance workflows"
      painPoints={[
        'Crew qualification data is usually spread across LMS records, simulator providers, spreadsheets, and manual compliance trackers.',
        'External provider completions are hard to reconcile with internal qualification decisions and recurrence status.',
        'Check-event debriefs and assessor approvals often sit outside the governed training system.',
        'Supervisors need a single view of license, medical, and training exceptions before they become operational risk.',
      ]}
      capabilities={[
        'Crew qualification profiles with role, rank, base, fleet, license, medical, and competency context through structured airline workspaces and row editors.',
        'External training import intake with mapped application into governed qualification records, including recurrence-policy-derived validity when applicable.',
        'Structured airline check events, debrief capture, and assessor-authority-based finalization in dedicated operational workspaces.',
        'Recurrence-alert queues, qualification decision review, training connectors, and supervisor compliance notifications.',
        'Export-ready airline record packs using named training export profiles, authority labels and codes, inclusion rules, and redaction controls.',
      ]}
      workflow={[
        {
          title: 'Build the airline qualification layer',
          description: 'Establish crew qualification profiles and qualification programs so airline training is tracked against governed operational requirements instead of isolated course history.',
          items: [
            'Crew profiles by role, base, and fleet',
            'Qualification programs and recurrence policies',
            'Assessor authority setup and connector-aware operational signoff',
          ],
        },
        {
          title: 'Bring in internal and external evidence',
          description: 'Run normal LMS delivery for internal training, then merge provider imports, check events, connector data, and debrief outcomes into the same governed record model.',
          items: [
            'Course delivery, enrollments, and certificates',
            'External provider imports with governed qualification-record application',
            'Check-event capture, debrief summaries, and qualification synchronization',
          ],
        },
        {
          title: 'Monitor readiness and export records',
          description: 'Surface recurrence alerts and compliance queues early, then produce airline-ready JSON record packs for downstream oversight, audits, and internal review.',
          items: [
            'Expired, due-soon, missing-validity, license, and medical-driven alerts',
            'Supervisor compliance queue with direct notification actions',
            'Structured airline exports per user or global scope',
          ],
        },
      ]}
      outcomes={[
        'A single governed source of truth for crew training evidence, qualification decisions, and recurrence state.',
        'Less manual reconciliation between airline LMS operations and external providers such as simulator or training partners.',
        'Better operational readiness because compliance exceptions become visible before they block crew planning or oversight review.',
      ]}
      keywords={[
        'LMS for Airlines',
        'airline LMS',
        'airline crew training LMS',
        'crew qualification tracking',
        'airline training compliance',
        'pilot training management system',
      ]}
      faq={[
        {
          question: 'What makes AviaGov LMS different for airlines?',
          answer: 'AviaGov combines course delivery with crew qualification profiles, external provider imports, structured check events, recurrence alerts, and governed qualification records instead of stopping at standard LMS completion tracking.',
        },
        {
          question: 'Can AviaGov LMS track crew qualification and recurrence?',
          answer: 'Yes. Airline deployments can define qualification programs, recurrence policies, and assessor authority controls, then monitor expired, due-soon, and missing-validity items through recurrence alerts and compliance queues.',
        },
        {
          question: 'Does the airline LMS support external training providers?',
          answer: 'Yes. AviaGov can intake external training imports, map them into governed review flows, and apply approved events into qualification records for a unified training history.',
        },
        {
          question: 'Can airline check events and debriefs be recorded in the LMS?',
          answer: 'Yes. AviaGov supports structured check-event and debrief workflows tied to qualification programs and assessor authority so qualification finalization stays controlled and auditable.',
        },
      ]}
      relatedLinks={relatedLinks}
      ctaId="lms_airlines_walkthrough"
    />
  );
};

export default LmsAirlinesPage;
